export interface PolicyBlock {
  type: "paragraph" | "list" | "subheading" | "contact-box";
  text?: string;
  items?: string[];
  title?: string;
  companyName?: string;
  email?: string;
  website?: string;
  address?: string;
}

export interface PolicySection {
  id: string;
  title: string;
  blocks: PolicyBlock[];
}

export const privacyPolicySections: PolicySection[] = [
  {
    id: "introduction",
    title: "1. Introduction",
    blocks: [
      {
        type: "paragraph",
        text: "Welcome to Meegloo (“Meegloo”, “we”, “our”, or “us”).",
      },
      {
        type: "paragraph",
        text: "Meegloo is a technology-enabled residential construction management platform that helps homeowners plan, manage, monitor, and execute home construction projects through a network of verified contractors, consultants, architects, engineers, and service providers.",
      },
      {
        type: "paragraph",
        text: "We are committed to protecting your privacy and ensuring that your personal information is handled responsibly and securely.",
      },
      {
        type: "paragraph",
        text: "This Privacy Policy explains how we collect, use, store, share, and protect your information when you visit our website, submit a lead form, request a consultation, or use our services.",
      },
      {
        type: "paragraph",
        text: "By using our website or services, you agree to the practices described in this Privacy Policy.",
      },
    ],
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    blocks: [
      {
        type: "subheading",
        title: "A. Information You Provide",
      },
      {
        type: "paragraph",
        text: "We may collect information that you voluntarily provide, including:",
      },
      {
        type: "list",
        items: [
          "Full name",
          "Email address",
          "Mobile number",
          "WhatsApp number",
          "Property location",
          "Plot details",
          "Construction requirements",
          "Budget information",
          "Project timelines",
          "Any information submitted through forms, surveys, consultations, or communications",
        ],
      },
      {
        type: "subheading",
        title: "B. Information Collected Automatically",
      },
      {
        type: "paragraph",
        text: "When you visit our website, we may automatically collect:",
      },
      {
        type: "list",
        items: [
          "IP address",
          "Browser type",
          "Device information",
          "Pages visited",
          "Session information",
          "Referral source",
          "Approximate geographic location",
        ],
      },
      {
        type: "subheading",
        title: "C. Project Information",
      },
      {
        type: "paragraph",
        text: "If you engage with Meegloo for a construction project, we may collect:",
      },
      {
        type: "list",
        items: [
          "Property details",
          "Architectural drawings",
          "Project specifications",
          "Budget estimates",
          "Project documentation",
          "Site photographs",
          "Construction progress information",
        ],
      },
    ],
  },
  {
    id: "how-we-use-your-information",
    title: "3. How We Use Your Information",
    blocks: [
      {
        type: "paragraph",
        text: "We may use your information to:",
      },
      {
        type: "list",
        items: [
          "Respond to inquiries and consultation requests",
          "Provide construction cost estimates",
          "Understand your construction requirements",
          "Connect you with relevant contractors or service providers",
          "Manage and monitor construction projects",
          "Provide customer support",
          "Send project updates",
          "Improve our platform and services",
          "Conduct analytics and business reporting",
          "Prevent fraud and misuse",
          "Comply with legal obligations",
        ],
      },
    ],
  },
  {
    id: "lead-forms-and-advertising",
    title: "4. Lead Forms and Advertising",
    blocks: [
      {
        type: "paragraph",
        text: "If you submit information through Meta (Facebook/Instagram), Google, or other advertising platforms:",
      },
      {
        type: "list",
        items: [
          "We may contact you via phone, WhatsApp, SMS, or email.",
          "We may use the information to understand your project requirements.",
          "We may schedule consultations and provide construction-related guidance.",
          "We may store your information in our CRM or customer management systems.",
        ],
      },
      {
        type: "paragraph",
        text: "Submitting a lead form does not create any contractual obligation between you and Meegloo.",
      },
    ],
  },
  {
    id: "sharing-of-information",
    title: "5. Sharing of Information",
    blocks: [
      {
        type: "paragraph",
        text: "We do not sell your personal information.",
      },
      {
        type: "paragraph",
        text: "We may share information with:",
      },
      {
        type: "subheading",
        title: "Service Providers",
      },
      {
        type: "paragraph",
        text: "Including:",
      },
      {
        type: "list",
        items: [
          "Contractors",
          "Architects",
          "Engineers",
          "Consultants",
          "Construction service providers",
        ],
      },
      {
        type: "paragraph",
        text: "Only when necessary to evaluate, plan, or execute your project with your consent.",
      },
      {
        type: "subheading",
        title: "Technology Providers",
      },
      {
        type: "paragraph",
        text: "We may use trusted third-party vendors for:",
      },
      {
        type: "list",
        items: [
          "Website hosting",
          "CRM systems",
          "Analytics",
          "Communication services",
          "Cloud storage",
        ],
      },
      {
        type: "subheading",
        title: "Legal Requirements",
      },
      {
        type: "paragraph",
        text: "We may disclose information when required by law or governmental authorities.",
      },
    ],
  },
  {
    id: "cookies-and-analytics",
    title: "6. Cookies and Analytics",
    blocks: [
      {
        type: "paragraph",
        text: "We may use cookies and similar technologies to:",
      },
      {
        type: "list",
        items: [
          "Improve website functionality",
          "Analyze user behavior",
          "Measure advertising performance",
          "Personalize user experiences",
        ],
      },
      {
        type: "paragraph",
        text: "You may disable cookies through your browser settings, although some website features may not function properly.",
      },
    ],
  },
  {
    id: "data-security",
    title: "7. Data Security",
    blocks: [
      {
        type: "paragraph",
        text: "We implement reasonable technical, administrative, and organizational safeguards to protect personal information from:",
      },
      {
        type: "list",
        items: [
          "Unauthorized access",
          "Loss",
          "Misuse",
          "Disclosure",
          "Alteration",
        ],
      },
      {
        type: "paragraph",
        text: "However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      },
    ],
  },
  {
    id: "data-retention",
    title: "8. Data Retention",
    blocks: [
      {
        type: "paragraph",
        text: "We retain information for as long as necessary to:",
      },
      {
        type: "list",
        items: [
          "Provide our services",
          "Manage customer relationships",
          "Comply with legal obligations",
          "Resolve disputes",
          "Maintain business records",
        ],
      },
      {
        type: "paragraph",
        text: "We may delete or anonymize information when it is no longer required.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "9. Your Rights",
    blocks: [
      {
        type: "paragraph",
        text: "Subject to applicable laws, you may request:",
      },
      {
        type: "list",
        items: [
          "Access to your personal information",
          "Correction of inaccurate information",
          "Deletion of information where applicable",
          "Withdrawal of consent",
          "Restriction of processing",
        ],
      },
      {
        type: "paragraph",
        text: "To exercise these rights, please contact us using the details below.",
      },
    ],
  },
  {
    id: "third-party-links",
    title: "10. Third-Party Links",
    blocks: [
      {
        type: "paragraph",
        text: "Our website may contain links to third-party websites or services.",
      },
      {
        type: "paragraph",
        text: "We are not responsible for the privacy practices, content, or security of third-party websites.",
      },
      {
        type: "paragraph",
        text: "Users are encouraged to review the privacy policies of those websites separately.",
      },
    ],
  },
  {
    id: "childrens-privacy",
    title: "11. Children’s Privacy",
    blocks: [
      {
        type: "paragraph",
        text: "Our services are intended for adults.",
      },
      {
        type: "paragraph",
        text: "We do not knowingly collect personal information from children under the age of 18.",
      },
      {
        type: "paragraph",
        text: "If we become aware that such information has been collected, we will take appropriate steps to remove it.",
      },
    ],
  },
  {
    id: "changes-to-this-privacy-policy",
    title: "12. Changes to This Privacy Policy",
    blocks: [
      {
        type: "paragraph",
        text: "We may update this Privacy Policy from time to time.",
      },
      {
        type: "paragraph",
        text: "Any updates will be posted on this page with a revised “Last Updated” date.",
      },
      {
        type: "paragraph",
        text: "Continued use of our services after changes become effective constitutes acceptance of the updated policy.",
      },
    ],
  },
  {
    id: "contact-us",
    title: "13. Contact Us",
    blocks: [
      {
        type: "paragraph",
        text: "For questions regarding this Privacy Policy or your personal information, please contact:",
      },
      {
        type: "contact-box",
        companyName: "Meegloo",
        email: "info@meegloo.com",
        website: "https://www.meegloo.com",
        address: "Ghaziabad, India",
      },
    ],
  },
];
